import styled from '@emotion/styled';
import type { FabricEditor } from 'modules/fabric-editor';
import type { LogoSlot } from 'modules/manager/domain/logo';
import React, { useEffect, useReducer } from 'react';
import InspectorRow from '../shared/inspector-row';
import InspectorSection from '../shared/inspector-section';
import NumberField from '../shared/number-field';

interface MandatoryLogoSectionProps {
  editor: FabricEditor;
}

// 3×3 mini-field. The center is null — the logo lives only in the 8 edge slots.
const GRID: (LogoSlot | null)[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  null,
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

const MandatoryLogoSection: React.FC<MandatoryLogoSectionProps> = ({ editor }) => {
  const [, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const offModified = editor.on('object:modified', () => bump());
    const offLoaded = editor.on('canvas:loaded', () => bump());
    const offAdded = editor.on('object:added', () => bump());
    return () => {
      offModified();
      offLoaded();
      offAdded();
    };
  }, [editor]);

  const logo = editor.logo;
  if (!logo) return null;

  const current = logo.getPosition();
  const scaleBounds = logo.getScaleBounds();
  const marginBounds = logo.getMarginBounds();
  const minScalePercent = Math.round(scaleBounds.min * 100);
  const scalePercent = Math.round((logo.getScale() ?? 1) * 100);
  const margin = logo.getMargin() ?? 0;

  return (
    <InspectorSection title="Логотип ImGame">
      <Field>
        {GRID.map((slot, i) =>
          slot ? (
            <Slot
              key={slot}
              type="button"
              title={slot}
              active={slot === current}
              onClick={() => logo.setPosition(slot)}
            >
              <Dot active={slot === current} />
            </Slot>
          ) : (
            <Spacer key={`center-${i}`} />
          ),
        )}
      </Field>
      <InspectorRow label="Размер, %">
        <NumberField
          value={scalePercent}
          min={minScalePercent}
          step={5}
          emptyValue={100}
          onChange={(v) => logo.setScale(v / 100)}
        />
      </InspectorRow>
      <InspectorRow label="Отступ, px">
        <NumberField
          value={margin}
          min={marginBounds.min}
          max={marginBounds.max}
          step={4}
          emptyValue={marginBounds.min}
          onChange={(v) => logo.setMargin(v)}
        />
      </InspectorRow>
    </InspectorSection>
  );
};

export default MandatoryLogoSection;

const Field = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  aspect-ratio: 16 / 10;
  max-width: 140px;
  gap: 3px;
  padding: 5px;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Slot = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid ${(p) => (p.active ? '#2563eb' : 'transparent')};
  border-radius: 4px;
  background: ${(p) => (p.active ? 'rgba(37, 99, 235, 0.08)' : 'transparent')};
  cursor: pointer;

  &:hover {
    background: rgba(37, 99, 235, 0.06);
  }
`;

const Spacer = styled.div``;

const Dot = styled.span<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${(p) => (p.active ? '#2563eb' : '#9ca3af')};
`;
